import { abortable, throwIfAborted } from './exportImages';
import { escapeHtml } from './prepareNoteHtml';

export function openPrintWindow(preparing: string): Window | null {
  const popup = window.open('', '_blank', 'popup');
  if (!popup) return null;
  popup.opener = null;
  popup.document.open();
  popup.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(preparing)}</title></head><body><p role="status">${escapeHtml(preparing)}</p></body></html>`);
  popup.document.close();
  return popup;
}

export async function printNoteHtml(popup: Window, html: string, signal: AbortSignal): Promise<void> {
  throwIfAborted(signal);
  if (popup.closed) throw new DOMException('Print window closed', 'AbortError');
  popup.document.open();
  popup.document.write(html);
  popup.document.close();
  await abortable(new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error('Print document timed out'));
    }, 15000);
    const check = () => {
      if (popup.closed || signal.aborted) {
        cleanup();
        reject(new DOMException('Print cancelled', 'AbortError'));
      } else if (popup.document.readyState === 'complete') {
        cleanup();
        resolve();
      }
    };
    const interval = window.setInterval(check, 50);
    const cleanup = () => {
      window.clearTimeout(timer);
      window.clearInterval(interval);
      signal.removeEventListener('abort', check);
    };
    signal.addEventListener('abort', check, { once: true });
    check();
  }), signal);
  const decoding = new AbortController();
  const abortDecoding = () => decoding.abort();
  signal.addEventListener('abort', abortDecoding, { once: true });
  const decodeTimeout = window.setTimeout(abortDecoding, 15000);
  try {
    await abortable(Promise.all(Array.from(popup.document.images, (image) => image.decode())), decoding.signal);
  } finally {
    window.clearTimeout(decodeTimeout);
    signal.removeEventListener('abort', abortDecoding);
  }
  throwIfAborted(signal);
  if (popup.closed) throw new DOMException('Print window closed', 'AbortError');
  popup.focus();
  popup.print();
}

export function downloadNoteHtml(html: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  try {
    document.body.appendChild(anchor);
    anchor.click();
  } finally {
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
